import { Box, Card, Chip, Grid, Tab, Tabs, Typography, useTheme, Avatar, LinearProgress } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { DateRange, ButtonBasic, SelectUser } from "../../components";
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import AnaliticaAsesorService from "../../services/analiticaAsesor.service";
import FechaFunction from "../../function/FechaFunction";

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`analitica-tabpanel-${index}`}
            aria-labelledby={`analitica-tab-${index}`}
            {...other}
            style={{ maxHeight: '65vh', overflow: 'auto' }}
            className='side'
        >
            {value === index && (
                <Box sx={{ p: 2 }}>{children}</Box>
            )}
        </div>
    );
}
function a11yProps(index) {
    return {
        id: `analitica-tab-${index}`,
        'aria-controls': `analitica-tabpanel-${index}`,
    };
}

/* ----- Card KPI ----- */
const KpiCard = ({ title, value, icon, color, subtitle, isDarkMode }) => (
    <Card sx={{
        p: 2.5,
        borderRadius: 3,
        background: isDarkMode ? '#2d3a52' : '#fff',
        boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#90a4ae' : '#666', fontSize: 12, mb: 0.5 }}>
                    {title}
                </Typography>
                <Typography variant="h4" sx={{ color: isDarkMode ? '#e0e0e0' : '#1a1a1a', fontWeight: 'bold' }}>
                    {value}
                </Typography>
            </Box>
            <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${color}20`
            }}>
                {icon}
            </Box>
        </Box>
        {subtitle && (
            <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999', mt: 1 }}>
                {subtitle}
            </Typography>
        )}
    </Card>
);

/* ----- Distribución bar horizontal ----- */
const DistribucionCard = ({ title, data, isDarkMode, colors }) => {
    const total = Object.values(data).reduce((a, b) => a + b, 0);
    if (total === 0) return null;
    const colorsArr = colors || ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4', '#e91e63', '#607d8b', '#795548', '#8bc34a'];
    const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]);
    return (
        <Card sx={{
            p: 2.5, borderRadius: 3, mb: 2,
            background: isDarkMode ? '#2d3a52' : '#fff',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
        }}>
            <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold', mb: 2 }}>
                {title}
            </Typography>
            {sorted.map(([label, val], idx) => {
                const pct = ((val / total) * 100).toFixed(1);
                const barColor = colorsArr[idx % colorsArr.length];
                return (
                    <Box key={label} sx={{ mb: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" mb={0.3}>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#b0bec5' : '#555' }}>
                                {label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#90a4ae' : '#777', fontWeight: 500 }}>
                                {val} ({pct}%)
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={parseFloat(pct)}
                            sx={{
                                height: 8, borderRadius: 4,
                                backgroundColor: isDarkMode ? '#1a2332' : '#f0f0f0',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    backgroundColor: barColor,
                                }
                            }}
                        />
                    </Box>
                );
            })}
        </Card>
    );
};

/* ----- Timeline mini ----- */
const BAR_MAX_HEIGHT = 100;
const TimelineCard = ({ title, data, isDarkMode, color }) => {
    const entries = Object.entries(data).sort((a, b) => a[0].localeCompare(b[0]));
    if (entries.length === 0) return null;
    const maxVal = Math.max(...entries.map(e => e[1]), 1);
    const total = entries.reduce((acc, [, v]) => acc + v, 0);
    return (
        <Card sx={{
            p: 2.5, borderRadius: 3, mb: 2,
            background: isDarkMode ? '#2d3a52' : '#fff',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
            minHeight: 250
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold' }}>
                    {title}
                </Typography>
                <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>
                    Total: {total} &middot; Días: {entries.length}
                </Typography>
            </Box>
            <Box sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 1 }} className="side">
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'flex-end',
                    gap: '3px',
                    minWidth: '100%',
                    height: BAR_MAX_HEIGHT + 70,
                    pt: 2,
                }}>
                    {entries.map(([dia, val]) => {
                        const barH = Math.max(Math.round((val / maxVal) * BAR_MAX_HEIGHT), 4);
                        return (
                            <Box key={dia} sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                minWidth: entries.length > 60 ? 18 : 28,
                                flexShrink: 0,
                            }}>
                                <Typography variant="caption" sx={{
                                    color: isDarkMode ? '#90a4ae' : '#888',
                                    fontSize: entries.length > 60 ? 8 : 10,
                                    lineHeight: 1, mb: '2px',
                                    fontWeight: val === maxVal ? 700 : 400,
                                }}>
                                    {val}
                                </Typography>
                                <Box sx={{
                                    width: entries.length > 60 ? 12 : 18,
                                    height: barH,
                                    borderRadius: '3px 3px 0 0',
                                    background: val === maxVal
                                        ? (color || '#2196f3')
                                        : `${color || '#2196f3'}99`,
                                    transition: 'height 0.3s ease',
                                    '&:hover': {
                                        background: color || '#2196f3',
                                        transform: 'scaleX(1.3)',
                                    }
                                }} />
                                <Typography variant="caption" sx={{
                                    color: isDarkMode ? '#78909c' : '#aaa',
                                    fontSize: entries.length > 60 ? 7 : 9,
                                    lineHeight: 1, mt: '3px',
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'mixed',
                                    maxHeight: 56,
                                    overflow: 'hidden',
                                }}>
                                    {dia.split('-').reverse().join('/')}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Card>
    );
};

/* ----- Tabla simple de detalle ----- */
const DetalleTable = ({ columns, rows, isDarkMode }) => {
    if (!rows || rows.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: isDarkMode ? '#78909c' : '#999', textAlign: 'center', py: 4 }}>
                Sin datos para mostrar
            </Typography>
        );
    }
    return (
        <Box sx={{ overflow: 'auto', maxHeight: 400 }} className="side">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th key={col.key} style={{
                                padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600,
                                color: isDarkMode ? '#90a4ae' : '#666',
                                borderBottom: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
                                position: 'sticky', top: 0,
                                background: isDarkMode ? '#2d3a52' : '#fff',
                            }}>
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? (isDarkMode ? '#253044' : '#fafafa') : 'transparent' }}>
                            {columns.map((col) => (
                                <td key={col.key} style={{
                                    padding: '6px 12px', fontSize: 12,
                                    color: isDarkMode ? '#b0bec5' : '#444',
                                    borderBottom: `1px solid ${isDarkMode ? '#2a3548' : '#f0f0f0'}`
                                }}>
                                    {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '-')}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </Box>
    );
};

/* ============================================ */
/* PANTALLA PRINCIPAL                           */
/* ============================================ */
const AnaliticaAsesorIndividualScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [asesores, setAsesores] = useState([]);
    const [selectedAsesor, setSelectedAsesor] = useState(null);
    const [fechaSelect, setFechaSelect] = useState(() => {
        const hoy = new Date();
        const hace7Dias = new Date();
        hace7Dias.setDate(hoy.getDate() - 7);
        return {
            startDate: hace7Dias,
            endDate: hoy
        };
    });

    // Cargar lista de asesores
    useEffect(() => {
        AnaliticaAsesorService.getAsesoresLista().then((res) => {
            setAsesores(res?.data || res || []);
        }).catch(console.error);
    }, []);

    const asesorOptions = useMemo(() =>
        asesores.map(a => ({ value: a.id_p, label: a.nombre_p, item: a })),
    [asesores]);

    // Buscar analítica
    const buscarAnalitica = () => {
        if (!selectedAsesor) return;
        setLoading(true);
        const body = {
            asesorId: selectedAsesor,
            fecIni: FechaFunction.format(fechaSelect.startDate, 'yyyy-MM-dd'),
            fecFin: FechaFunction.format(fechaSelect.endDate, 'yyyy-MM-dd'),
        };
        AnaliticaAsesorService.getAnaliticaIndividual(body).then((res) => {
            setData(res?.data || res);
        }).catch((err) => {
            console.error(err);
            setData(null);
        }).finally(() => setLoading(false));
    };

    useEffect(() => {
        document.title = 'Analítica - Asesor Individual';
    }, []);

    const selectStyles = {
        control: (base) => ({
            ...base,
            background: isDarkMode ? '#1a2332' : '#fff',
            borderColor: isDarkMode ? '#3d4a62' : '#ccc',
            color: isDarkMode ? '#e0e0e0' : '#333',
            minHeight: 42,
        }),
        singleValue: (base) => ({ ...base, color: isDarkMode ? '#e0e0e0' : '#333' }),
        menu: (base) => ({ ...base, background: isDarkMode ? '#2d3a52' : '#fff', zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            background: state.isFocused ? (isDarkMode ? '#3d4a62' : '#f0f0f0') : 'transparent',
            color: isDarkMode ? '#e0e0e0' : '#333',
        }),
        input: (base) => ({ ...base, color: isDarkMode ? '#e0e0e0' : '#333' }),
    };

    /* ----- Columnas tabla tickets ----- */
    const ticketCols = [
        { key: 'id_tk', label: 'ID' },
        { key: 'titulo_tk', label: 'Título' },
        { key: 'estado_tk', label: 'Estado', render: (v) => <Chip label={v} size="small" color={v === 'ABIERTO' ? 'warning' : 'success'} variant="outlined" /> },
        { key: 'stageTicket_tk', label: 'Stage' },
        { key: 'tipo', label: 'Tipo' },
        { key: 'motivo', label: 'Motivo' },
        { key: 'createdAt', label: 'Creado' },
    ];

    /* ----- Columnas tabla ventas ----- */
    const ventaCols = [
        { key: 'id_t', label: 'ID' },
        { key: 'nombre_t', label: 'Cliente' },
        { key: 'estadoServicio_t', label: 'Estado', render: (v) => {
            const color = ['BAJA','CANCELADO'].includes(v?.toUpperCase?.()) ? 'error' : 'success';
            return <Chip label={v || '-'} size="small" color={color} variant="outlined" />;
        }},
        { key: 'categoria_t', label: 'Categoría' },
        { key: 'tipoTecnologia_t', label: 'Tecnología' },
        { key: 'plan', label: 'Plan' },
        { key: 'createdAt', label: 'Creado' },
    ];

    /* ----- Columnas tabla consultas ----- */
    const consultaCols = [
        { key: 'id_con', label: 'ID' },
        { key: 'nombreContacto_con', label: 'Contacto' },
        { key: 'estadoConsulta_con', label: 'Estado' },
        { key: 'subEstadoConsulta_con', label: 'Sub-Estado' },
        { key: 'canal_con', label: 'Canal' },
        { key: 'createdAt', label: 'Creado' },
    ];

    return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'auto', background: isDarkMode ? '#1a2332' : '#f5f5f5' }} className='side'>
            <Grid container p={3} spacing={2}>
                {/* Header */}
                <Grid item xs={12}>
                    <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#000', fontWeight: 'bold' }} variant="h4">
                        Analítica Individual de Asesor
                    </Typography>
                </Grid>

                {/* Filtros */}
                <Grid item xs={12}>
                    <Card sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        background: isDarkMode ? '#2d3a52' : '#fff', 
                        boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                        border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`
                    }}>
                        <Grid container spacing={3} alignItems="flex-end">
                            <Grid item xs={12} sm={6} md={5}>
                                <Typography variant="caption" sx={{ 
                                    color: isDarkMode ? '#90a4ae' : '#666', 
                                    mb: 1, 
                                    display: 'block',
                                    fontWeight: 600,
                                    fontSize: 13,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5
                                }}>
                                    Seleccionar Asesor
                                </Typography>
                                <SelectUser
                                    value={selectedAsesor}
                                    onChange={(e)=>{console.log(e); setSelectedAsesor(e?.value)}}
                                    placeholder="Buscar asesor..."
                                    isClearable
                                    filter='asesores'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={5}>
                                <Typography variant="caption" sx={{ 
                                    color: isDarkMode ? '#90a4ae' : '#666', 
                                    mb: 1, 
                                    display: 'block',
                                    fontWeight: 600,
                                    fontSize: 13,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5
                                }}>
                                    Rango de Fechas
                                </Typography>
                                <DateRange
                                    minDate={new Date('2024-01-01')}
                                    initianDate={fechaSelect}
                                    onChange={setFechaSelect}
                                    maxDays={180}
                                />
                                <Typography variant="caption" sx={{ 
                                    color: isDarkMode ? '#78909c' : '#999', 
                                    mt: 0.5,
                                    display: 'block',
                                    fontSize: 11
                                }}>
                                    Máximo 180 días
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <ButtonBasic
                                    textButton="Buscar"
                                    variant='wk'
                                    color='nextStep'
                                    iconStart={<SearchIcon />}
                                    onClick={buscarAnalitica}
                                    isLoading={loading}
                                    isDisabled={!selectedAsesor || loading}
                                    sx={{ 
                                        height: 42,
                                        width: '100%',
                                        fontWeight: 600,
                                        fontSize: 14
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                {/* Info Asesor */}
                {data?.asesor && (
                    <Grid item xs={12}>
                        <Card sx={{ p: 2, borderRadius: 3, background: isDarkMode ? '#2d3a52' : '#fff', boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar
                                    src={data.asesor.foto_p ? `/uploads/fotos/${data.asesor.foto_p}` : undefined}
                                    sx={{ width: 48, height: 48, bgcolor: '#2196f3' }}
                                >
                                    {data.asesor.nombre_p?.[0] || 'A'}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold' }}>
                                        {data.asesor.nombre_p}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>
                                        {data.asesor.departamento_p} &middot; Período: {data.periodo?.desde?.split('-').reverse().join('/')} al {data.periodo?.hasta?.split('-').reverse().join('/')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>
                )}

                {/* Si no hay asesor seleccionado */}
                {!selectedAsesor && !loading && (
                    <Grid item xs={12}>
                        <Card sx={{ p: 6, borderRadius: 3, background: isDarkMode ? '#2d3a52' : '#fff', textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>
                                Seleccione un asesor para ver su analítica
                            </Typography>
                        </Card>
                    </Grid>
                )}

                {/* KPIs */}
                {data?.resumen && (
                    <>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Tickets Creados"
                                value={data.resumen.totalTicketsCreados}
                                icon={<ConfirmationNumberOutlinedIcon sx={{ color: '#2196f3' }} />}
                                color="#2196f3"
                                subtitle={`${data.resumen.ticketsAbiertos} abiertos · ${data.resumen.ticketsCerrados} cerrados`}
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Tickets Cerrados (por asesor)"
                                value={data.resumen.totalTicketsCerradosPorAsesor}
                                icon={<CheckCircleOutlineIcon sx={{ color: '#4caf50' }} />}
                                color="#4caf50"
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Tickets Vencidos"
                                value={data.resumen.ticketsVencidos}
                                icon={<WarningAmberIcon sx={{ color: '#ff9800' }} />}
                                color="#ff9800"
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Total Ventas"
                                value={data.resumen.totalVentas}
                                icon={<PointOfSaleOutlinedIcon sx={{ color: '#9c27b0' }} />}
                                color="#9c27b0"
                                subtitle={`${data.resumen.ventasActivas} activas`}
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Consultas"
                                value={data.resumen.totalConsultas}
                                icon={<QuestionAnswerOutlinedIcon sx={{ color: '#00bcd4' }} />}
                                color="#00bcd4"
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Comentarios"
                                value={data.resumen.totalComentarios}
                                icon={<CommentOutlinedIcon sx={{ color: '#607d8b' }} />}
                                color="#607d8b"
                                isDarkMode={isDarkMode}
                            />
                        </Grid>

                        {/* Tabs de detalle */}
                        <Grid item xs={12}>
                            <Card sx={{
                                background: isDarkMode ? '#2d3a52' : '#fff',
                                borderRadius: 3, mt: 1,
                                boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                            }}>
                                <Box sx={{ borderBottom: 1, borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0' }}>
                                    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable"
                                        sx={{
                                            '& .MuiTab-root': { color: isDarkMode ? '#90a4ae' : '#666', textTransform: 'none', fontWeight: 500 },
                                            '& .Mui-selected': { color: isDarkMode ? '#64b5f6 !important' : '#1976d2 !important' },
                                            '& .MuiTabs-indicator': { backgroundColor: isDarkMode ? '#64b5f6' : '#1976d2' },
                                        }}
                                    >
                                        <Tab label={`Tickets (${data.resumen.totalTicketsCreados})`} {...a11yProps(0)} sx={{ minWidth: 'fit-content', flex: 1 }} />
                                        <Tab label={`Ventas (${data.resumen.totalVentas})`} {...a11yProps(1)} sx={{ minWidth: 'fit-content', flex: 1 }} />
                                        <Tab label={`Consultas (${data.resumen.totalConsultas})`} {...a11yProps(2)} sx={{ minWidth: 'fit-content', flex: 1 }} />
                                    </Tabs>
                                </Box>

                                {/* TAB TICKETS */}
                                <TabPanel value={tab} index={0}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Stage" data={data.tickets?.porStage || {}} isDarkMode={isDarkMode} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Tipo" data={data.tickets?.porTipo || {}} isDarkMode={isDarkMode}
                                                colors={['#4caf50', '#ff9800', '#f44336', '#2196f3', '#9c27b0', '#00bcd4']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Motivo" data={data.tickets?.porMotivo || {}} isDarkMode={isDarkMode}
                                                colors={['#e91e63', '#3f51b5', '#009688', '#ff5722', '#795548', '#607d8b']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por SubTipo" data={data.tickets?.porSubTipo || {}} isDarkMode={isDarkMode}
                                                colors={['#00bcd4', '#8bc34a', '#ffc107', '#673ab7', '#ff5722', '#9e9e9e']}
                                            />
                                        </Grid>
                                        {Object.keys(data.tickets?.porMotivoBaja || {}).length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <DistribucionCard title="Por Motivo de Baja" data={data.tickets.porMotivoBaja} isDarkMode={isDarkMode}
                                                    colors={['#f44336', '#e91e63', '#ff5722', '#ff9800', '#795548']}
                                                />
                                            </Grid>
                                        )}
                                        {Object.keys(data.tickets?.porCierre || {}).length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <DistribucionCard title="Por Cierre" data={data.tickets.porCierre} isDarkMode={isDarkMode}
                                                    colors={['#4caf50', '#8bc34a', '#cddc39', '#009688', '#00bcd4']}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <TimelineCard title="Tickets por Día" data={data.tickets?.porDia || {}} isDarkMode={isDarkMode} color="#2196f3" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Card
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 3,
                                                    background: isDarkMode ? '#2d3a52' : '#fff',
                                                    boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                                                    border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
                                                    mb: 2
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold', mb: 2 }}>
                                                    Detalle de Tickets
                                                </Typography>
                                                <DetalleTable columns={ticketCols} rows={data.tickets?.detalle || []} isDarkMode={isDarkMode} />
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{width: 100, height: 100}} />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                {/* TAB VENTAS */}
                                <TabPanel value={tab} index={1}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Estado" data={data.ventas?.porEstado || {}} isDarkMode={isDarkMode}
                                                colors={['#4caf50', '#ff9800', '#f44336', '#9c27b0', '#607d8b']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Planes" data={data.ventas?.porPlanes || {}} isDarkMode={isDarkMode}
                                                colors={['#2196f3', '#00bcd4', '#e91e63', '#ff5722', '#3f51b5', '#795548']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Tecnología" data={data.ventas?.porTecnologia || {}} isDarkMode={isDarkMode}
                                                colors={['#2196f3', '#4caf50', '#ff9800', '#9c27b0']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Categoría" data={data.ventas?.porCategoria || {}} isDarkMode={isDarkMode}
                                                colors={['#e91e63', '#3f51b5', '#009688', '#ff5722']}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TimelineCard title="Ventas por Día" data={data.ventas?.porDia || {}} isDarkMode={isDarkMode} color="#9c27b0" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Card sx={{ p: 2, borderRadius: 3, background: isDarkMode ? '#2d3a52' : '#fff', boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}` }}>
                                                <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold', mb: 2 }}>
                                                    Detalle de Ventas
                                                </Typography>
                                                <DetalleTable columns={ventaCols} rows={data.ventas?.detalle || []} isDarkMode={isDarkMode} />
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{width: 100, height: 100}} />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                {/* TAB CONSULTAS */}
                                <TabPanel value={tab} index={2}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Estado" data={data.consultas?.porEstado || {}} isDarkMode={isDarkMode}
                                                colors={['#00bcd4', '#4caf50', '#ff9800', '#f44336', '#9c27b0']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TimelineCard title="Consultas por Día" data={data.consultas?.porDia || {}} isDarkMode={isDarkMode} color="#00bcd4" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Card sx={{ p: 2, borderRadius: 3, background: isDarkMode ? '#2d3a52' : '#fff', boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}` }}>
                                                <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold', mb: 2 }}>
                                                    Detalle de Consultas
                                                </Typography>
                                                <DetalleTable columns={consultaCols} rows={data.consultas?.detalle || []} isDarkMode={isDarkMode} />
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{width: 100, height: 100}} />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                            </Card>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default AnaliticaAsesorIndividualScreen;
